import { useRef, useState } from "react";
import { Button } from "../../../design-v2";
import { removeUserAvatar, uploadUserAvatar } from "../../../services/api";
import ProfileAvatar from "./ProfileAvatar";

// Mirrors the backend contract so the obvious rejections cost no round trip.
// The backend remains the sole authority: it re-checks size by bytes and type
// by content, and a client that skips these checks is simply refused there.
const MAX_UPLOAD_BYTES = 5242880;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.join(",");

const CLIENT_TOO_LARGE = "That image is larger than 5 MB. Choose a smaller file.";
const CLIENT_WRONG_TYPE = "Use a JPEG, PNG or WebP image.";

// One line of copy per code in the Phase C taxonomy, so a user never sees a
// raw backend message or an opaque failure.
const ERROR_COPY = {
  PAYLOAD_TOO_LARGE: CLIENT_TOO_LARGE,
  UNSUPPORTED_IMAGE_TYPE: CLIENT_WRONG_TYPE,
  IMAGE_DIMENSIONS_REJECTED:
    "That image is too large to process. Try one with smaller dimensions.",
  IMAGE_NORMALIZATION_FAILED: "That image could not be processed. Try a different file.",
  AVATAR_PROCESSOR_BUSY:
    "Another photo is being processed right now. Try again in a moment.",
  AVATAR_RATE_LIMITED:
    "Profile photo updates are temporarily limited. Please try again later.",
  MEDIA_STORAGE_UNAVAILABLE: "Photo storage is temporarily unavailable. Try again shortly.",
  MEDIA_DISABLED: "Profile photos are unavailable right now.",
  DATABASE_UNAVAILABLE: "Your profile could not be updated. Try again shortly.",
  CSRF_TOKEN_INVALID: "Your session expired. Refresh the page and try again.",
  CSRF_ORIGIN_REJECTED: "Your session expired. Refresh the page and try again.",
};

function errorCopy(error, fallback) {
  return ERROR_COPY[error?.code] || error?.message || fallback;
}

export function validateAvatarFile(file) {
  if (!file) {
    return null;
  }
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return CLIENT_WRONG_TYPE;
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return CLIENT_TOO_LARGE;
  }
  return null;
}

export default function ProfileAvatarSection({
  avatar = null,
  sex = null,
  canUploadAvatar = true,
  onAccountChange,
}) {
  const [pendingAction, setPendingAction] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const avatarUrl = avatar?.url || null;
  const hasCustomAvatar = Boolean(avatarUrl);
  const isBusy = pendingAction !== null;

  function resetInput() {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const clientError = validateAvatarFile(file);
    if (clientError) {
      // Rejected without a request at all.
      setError(clientError);
      resetInput();
      return;
    }

    setPendingAction("upload");
    setError("");
    try {
      const response = await uploadUserAvatar(file);
      // The endpoint returns the complete account block, so Settings state is
      // updated from the response rather than by re-fetching.
      onAccountChange?.(response.account);
    } catch (uploadError) {
      setError(errorCopy(uploadError, "Unable to update your photo."));
    } finally {
      setPendingAction(null);
      resetInput();
    }
  }

  async function handleRemove() {
    setPendingAction("remove");
    setError("");
    try {
      const response = await removeUserAvatar();
      onAccountChange?.(response.account);
    } catch (removeError) {
      setError(errorCopy(removeError, "Unable to remove your photo."));
    } finally {
      setPendingAction(null);
    }
  }

  return (
    /*
     * A compact identity block rather than a label/value row: the mark leads,
     * the field name sits beside it, and the controls occupy one full-width
     * line beneath so both fit on a 320 px viewport without shrinking type.
     *
     * When the deployment cannot accept photos, the same block carries one
     * short inline line in place of the controls. The unavailable state stays
     * exactly as authoritative as before — it simply no longer dominates the
     * card.
     */
    <div className="lz-settings-avatar-block" data-testid="profile-avatar-section">
      <span className="lz-settings-avatar-block__media">
        <ProfileAvatar avatarUrl={avatarUrl} sex={sex} size={56} />
      </span>

      <div className="lz-settings-avatar-block__copy">
        <p className="lz-settings-avatar-block__label">Profile Picture</p>
        {canUploadAvatar ? null : (
          <p className="lz-settings-avatar-block__note">
            Photo uploads are unavailable right now.
          </p>
        )}
      </div>

      {canUploadAvatar ? (
        <div className="lz-settings-avatar-block__actions">
          <input
            ref={inputRef}
            id="profile-avatar-input"
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            className="sr-only"
            onChange={handleFileChange}
            disabled={isBusy}
          />
          {/* The visible control drives the native file picker, so the input
              itself stays off-screen while remaining focusable by its id. */}
          <Button
            type="button"
            variant="secondary"
            className="lz-settings-inline-action"
            disabled={isBusy}
            isLoading={pendingAction === "upload"}
            loadingLabel="Uploading..."
            onClick={() => inputRef.current?.click()}
          >
            Change photo
          </Button>

          {/* Only meaningful when there is something to remove. */}
          {hasCustomAvatar ? (
            <Button
              type="button"
              variant="secondary"
              className="lz-settings-inline-action"
              disabled={isBusy}
              isLoading={pendingAction === "remove"}
              loadingLabel="Removing..."
              onClick={handleRemove}
            >
              Remove photo
            </Button>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="lz-settings-avatar-block__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export { ACCEPTED_TYPES, ERROR_COPY, MAX_UPLOAD_BYTES };
